import { completeHexColor } from '@/lib/color/hexColorInput';
import { pickContrastingColor } from '@/lib/color/contrast';
import { hslToHex } from '@/lib/color/hslToHex';

export type ColorHarmonyKind =
  | 'analogous'
  | 'complementary'
  | 'triadic'
  | 'splitComplementary';

export interface GenerateHarmoniousPaletteOptions {
  /** Number of swatches to return (2–8). Defaults to 4. */
  count?: number;
  /** Injectable RNG for tests. Defaults to Math.random. */
  random?: () => number;
}

export interface HarmoniousPaletteResult {
  colors: string[];
  harmony: ColorHarmonyKind;
  baseHue: number;
  /** Subtitle font color chosen to contrast with the first swatch. */
  fontColor: string;
}

const HARMONIES: ColorHarmonyKind[] = [
  'analogous',
  'complementary',
  'triadic',
  'splitComplementary',
];

/** Supported palette sizes when the user clicks Randomize. */
export const PREDEFINED_PALETTE_SIZES = [3, 4, 5, 6] as const;

export type PredefinedPaletteSize = (typeof PREDEFINED_PALETTE_SIZES)[number];

export function pickRandomPaletteSize(random: () => number = Math.random): PredefinedPaletteSize {
  const index = Math.floor(random() * PREDEFINED_PALETTE_SIZES.length);
  return PREDEFINED_PALETTE_SIZES[index] ?? PREDEFINED_PALETTE_SIZES[0];
}

function clampCount(count: number | undefined): number {
  if (count == null || !Number.isFinite(count)) return 4;
  return Math.max(2, Math.min(8, Math.round(count)));
}

function normalizeHue(hue: number): number {
  return ((hue % 360) + 360) % 360;
}

function harmonyHues(kind: ColorHarmonyKind, baseHue: number, count: number): number[] {
  const base = normalizeHue(baseHue);

  const seeds: Record<ColorHarmonyKind, number[]> = {
    analogous: [0, 22, 44, 66, 88, 110, 132, 154],
    complementary: [0, 180, 24, 204, 12, 192, 36, 216],
    triadic: [0, 120, 240, 60, 180, 300, 90, 210],
    splitComplementary: [0, 150, 210, 30, 330, 90, 120, 270],
  };

  return seeds[kind].slice(0, count).map((offset) => normalizeHue(base + offset));
}

function accentLightness(index: number, count: number, random: () => number): number {
  if (count >= 4 && index === count - 1 && random() < 0.35) {
    return 88 + random() * 8;
  }
  return 46 + random() * 14;
}

function accentSaturation(random: () => number): number {
  return 68 + random() * 22;
}

/** Builds a palette from classic color-harmony relationships (analogous,
 *  complementary, triadic, split-complementary). */
export function generateHarmoniousPalette(
  options: GenerateHarmoniousPaletteOptions = {},
): HarmoniousPaletteResult {
  const random = options.random ?? Math.random;
  const count = clampCount(options.count);
  const harmony = HARMONIES[Math.floor(random() * HARMONIES.length)]!;
  const baseHue = random() * 360;
  const hues = harmonyHues(harmony, baseHue, count);

  const colors = hues.map((hue, index) => {
    const hex = hslToHex(hue, accentSaturation(random), accentLightness(index, count, random));
    return completeHexColor(hex) ?? hex;
  });

  const highlightColor = colors[0] ?? '#ffffff';
  const fontColor = pickContrastingColor(highlightColor, random);

  return { colors, harmony, baseHue: normalizeHue(baseHue), fontColor };
}

/** Picks a predefined palette size, then builds a harmonious set. */
export function generateRandomHarmoniousPalette(
  options: Omit<GenerateHarmoniousPaletteOptions, 'count'> = {},
): HarmoniousPaletteResult {
  const random = options.random ?? Math.random;
  return generateHarmoniousPalette({ ...options, count: pickRandomPaletteSize(random) });
}
